import { type ReactNode, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  CommentCategory,
  CommentDecision,
  CommentDecisionState,
  CommentPlatform,
  CommentStance,
  useHideCommentMutation,
  useListCommentDecisionsQuery,
  useUnhideCommentMutation,
} from "~/ui-rtk/api/commentModerationApi"
import Table, {
  type TCellItem,
  type TTableHeaderProp,
} from "~/ui-rtk/components/ui/common/Table"
import Button from "~/ui-rtk/components/ui/controls/Button"
import { DebouncedInput } from "~/ui-rtk/components/ui/controls/DebouncedInput"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { INPUT_CLASSES, INPUT_CLS, SELECT_CLS } from "./ModerationSettings"
import { platformLabel } from "./reactions"

const PAGE_SIZE = 50

export const CATEGORY_LABEL: Record<CommentCategory, string> = {
  COMPETITOR: "Competitor",
  SPAM: "Spam",
  NEGATIVE: "Negative",
  QUESTION: "Question",
  PRAISE: "Praise",
  NEUTRAL: "Neutral",
  OTHER: "Other",
}

export const CATEGORY_CLS: Record<CommentCategory, string> = {
  COMPETITOR: "bg-rose-500/20 text-rose-300",
  SPAM: "bg-amber-500/20 text-amber-300",
  NEGATIVE: "bg-orange-500/20 text-orange-300",
  QUESTION: "bg-sky-500/20 text-sky-300",
  PRAISE: "bg-emerald-500/20 text-emerald-300",
  NEUTRAL: "bg-system-gray-500/20 text-system-gray-300",
  OTHER: "bg-system-gray-500/20 text-system-gray-300",
}

const STATE_OPTIONS: Array<{
  value: CommentDecisionState | ""
  label: string
}> = [
  { value: "", label: "All decisions" },
  { value: "hidden", label: "Hidden" },
  { value: "unhidden", label: "Unhidden" },
  { value: "failed", label: "Hide refused" },
  { value: "kept", label: "Kept visible" },
]

export function formatDateTime(iso?: string): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** What the row's hide state is, for the badge and the action button. */
function decisionState(d: CommentDecision): CommentDecisionState {
  if (d.verdict === "KEEP") return "kept"
  if (d.unhiddenAt) return "unhidden"
  if (d.hiddenAt || d.dryRun) return "hidden"
  return "failed"
}

/**
 * A HIDE verdict recorded while hiding was off: the API files it under the
 * "hidden" state, but nothing changed on Meta, so the row reads "Flagged" and
 * its action is to hide it now — never to unhide it.
 */
function isFlagged(d: CommentDecision): boolean {
  return d.verdict === "HIDE" && d.dryRun && !d.hiddenAt && !d.unhiddenAt
}

/** Who made the call, as the report shows it. */
function judgeLabel(d: CommentDecision): string {
  if (d.judgedBy === "RULE") return "rule"
  if (d.judgedBy === "USER") return "person"
  const pct = d.confidence != null ? ` ${Math.round(d.confidence * 100)}%` : ""
  return `AI${pct}`
}

/** The row's action button: what it does and the tooltip explaining it. */
const ACTION_FOR: Record<
  CommentDecisionState,
  { label: string; title: string }
> = {
  hidden: {
    label: "Unhide",
    title: "Make this comment visible to everyone again",
  },
  failed: { label: "Retry hide", title: "Retry hiding this comment" },
  kept: {
    label: "Hide",
    title: "Hide this comment (the writer still sees it)",
  },
  unhidden: {
    label: "Hide",
    title: "Hide this comment (the writer still sees it)",
  },
}

const STATE_BADGE: Record<
  CommentDecisionState,
  { label: string; cls: string }
> = {
  hidden: { label: "Hidden", cls: "bg-rose-500/20 text-rose-300" },
  unhidden: { label: "Unhidden", cls: "bg-sky-500/20 text-sky-300" },
  failed: { label: "Not hidden", cls: "bg-amber-500/20 text-amber-300" },
  kept: { label: "Visible", cls: "bg-emerald-500/20 text-emerald-300" },
}

/**
 * The judged comments of one window (optionally of one ad), newest first,
 * with state / category / text filters, paging, and one-click hide or
 * unhide per row. Three columns so it reads in the drawer: the comment with
 * who, when and where; why it was judged that way; its state and the action.
 */
export default function DecisionsTable({
  from,
  to,
  platform,
  adId,
  postId,
  showPost = true,
}: {
  from: string
  to?: string
  platform?: CommentPlatform
  adId?: string
  /** Only comments on this one post — the organic post drawer. */
  postId?: string
  /** Show the platform and a link to the post on each row. */
  showPost?: boolean
}) {
  const [state, setState] = useState<CommentDecisionState | "">("")
  const [category, setCategory] = useState<CommentCategory | "">("")
  const [stance, setStance] = useState<CommentStance | "">("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)

  const args = useMemo(
    () => ({
      from,
      to,
      platform,
      adId,
      postId,
      state: state || undefined,
      category: category || undefined,
      stance: stance || undefined,
      search: search || undefined,
      take: PAGE_SIZE,
      skip: page * PAGE_SIZE,
    }),
    [from, to, platform, adId, postId, state, category, stance, search, page],
  )
  const { data, isLoading, isFetching } = useListCommentDecisionsQuery(args)
  const [unhide, { isLoading: unhiding }] = useUnhideCommentMutation()
  const [hide, { isLoading: hiding }] = useHideCommentMutation()
  const [busyId, setBusyId] = useState<string | null>(null)

  const toggle = async (d: CommentDecision) => {
    const wantHidden = isFlagged(d) || decisionState(d) !== "hidden"
    setBusyId(d.id)
    try {
      await (wantHidden ? hide(d.id) : unhide(d.id)).unwrap()
      toast.success(
        wantHidden
          ? "Comment hidden on Meta."
          : "Comment is visible again on Meta.",
      )
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(
        message || `Could not ${wantHidden ? "hide" : "unhide"} the comment.`,
      )
    } finally {
      setBusyId(null)
    }
  }

  const headers: TTableHeaderProp[] = [
    {
      label: "Comment",
      cellClassName: "align-top",
      customRender: (item: TCellItem) => {
        const d = item as CommentDecision
        return (
          <div className="flex flex-col gap-1">
            <div className="text-sm text-system-gray-400">
              {d.authorName || "Someone"}
              {d.parentCommentId ? " · reply" : ""}
              {d.commentCreatedAt
                ? ` · ${formatDateTime(d.commentCreatedAt)}`
                : ""}
              {showPost && (
                <>
                  {" · "}
                  {d.permalinkUrl ? (
                    <a
                      href={d.permalinkUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-white"
                    >
                      {platformLabel(d.platform)} post
                    </a>
                  ) : (
                    platformLabel(d.platform)
                  )}
                </>
              )}
            </div>
            <div className="whitespace-pre-wrap break-words text-base text-system-gray-100">
              {d.message || <em className="text-system-gray-500">(no text)</em>}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-system-gray-400">
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${CATEGORY_CLS[d.category]}`}
              >
                {CATEGORY_LABEL[d.category]}
              </span>
              <span className="text-xs text-system-gray-500">
                {judgeLabel(d)}
              </span>
              <span>{d.reason}</span>
            </div>
            {d.hideError && decisionState(d) === "failed" && (
              <div className="text-sm text-amber-300">{d.hideError}</div>
            )}
          </div>
        )
      },
    },
    {
      label: "State",
      width: 150,
      align: "right",
      cellClassName: "align-top",
      customRender: (item: TCellItem) => {
        const d = item as CommentDecision
        const st = decisionState(d)
        const flagged = isFlagged(d)
        const badge = flagged
          ? { label: "Flagged", cls: "bg-amber-500/20 text-amber-200" }
          : STATE_BADGE[st]
        const actionLabel = flagged ? "Hide" : ACTION_FOR[st].label
        const busy = busyId === d.id && (unhiding || hiding)
        return (
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2 py-0.5 text-xs ${badge.cls}`}>
              {badge.label}
            </span>
            <span className="text-xs text-system-gray-500">
              {formatDateTime(d.unhiddenAt || d.hiddenAt || d.createdAt)}
            </span>
            <Button
              size="xs"
              variant={
                st === "hidden"
                  ? { variant: "text" }
                  : { variant: "solid", color: "red" }
              }
              onClick={() => void toggle(d)}
              disabled={busy}
              isLoading={busy}
              title={ACTION_FOR[st].title}
            >
              {actionLabel}
            </Button>
          </div>
        )
      },
    },
  ]

  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  let content: ReactNode
  if (isLoading) {
    content = (
      <p className="px-3 py-6 text-base text-system-gray-400">Loading…</p>
    )
  } else if (!data?.items.length) {
    content = (
      <p className="px-3 py-6 text-base text-system-gray-400">
        No judged comments match.
      </p>
    )
  } else {
    content = (
      <Table
        headers={headers}
        items={data.items}
        classes={{ th: "text-3.5", td: "text-base" }}
      />
    )
  }

  return (
    <div className="flex flex-col gap-system-xs">
      <div className="flex flex-wrap items-center gap-system-sm">
        <Select
          className={SELECT_CLS}
          value={state}
          onChange={e => {
            setState(e.target.value as CommentDecisionState | "")
            setPage(0)
          }}
          options={STATE_OPTIONS}
        />
        <Select
          className={SELECT_CLS}
          value={category}
          onChange={e => {
            setCategory(e.target.value as CommentCategory | "")
            setPage(0)
          }}
          options={[
            { label: "All categories", value: "" },
            ...(Object.keys(CATEGORY_LABEL) as CommentCategory[]).map(c => ({
              label: CATEGORY_LABEL[c],
              value: c,
            })),
          ]}
        />
        <Select
          className={SELECT_CLS}
          value={stance}
          onChange={e => {
            setStance(e.target.value as CommentStance | "")
            setPage(0)
          }}
          options={[
            { label: "Any stance", value: "" },
            { label: "Positive", value: "POSITIVE" },
            { label: "Negative", value: "NEGATIVE" },
            { label: "Questions", value: "QUESTION" },
            { label: "Neutral", value: "NEUTRAL" },
          ]}
        />
        <DebouncedInput
          type="search"
          width={192}
          className={INPUT_CLS}
          classes={INPUT_CLASSES}
          placeholder="Search comment text"
          defaultValue={search}
          onChange={e => {
            setSearch(e.target.value.trim())
            setPage(0)
          }}
        />
      </div>
      {content}
      <div className="flex items-center justify-between px-3 py-2 text-sm text-system-gray-400">
        <span>
          {total} comment{total === 1 ? "" : "s"}
          {isFetching ? " · refreshing…" : ""}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant={{ variant: "text" }}
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            ‹ Prev
          </Button>
          <span>
            {page + 1} / {pageCount}
          </span>
          <Button
            size="xs"
            variant={{ variant: "text" }}
            disabled={page + 1 >= pageCount}
            onClick={() => setPage(p => p + 1)}
          >
            Next ›
          </Button>
        </div>
      </div>
    </div>
  )
}
